'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'

// walrus
import Box from '@components/kit/box'
import Button from '@components/kit/button'
import Card from '@components/kit/card'
import FlatList from '@components/kit/flat-list'
import Pressable from '@components/kit/pressable'
import Text from '@components/kit/text'
import { accessibilityLabel } from '@components/kit/_support'
import { useNavigate } from 'react-router-dom'

// lib
import { ChatContext, ChatTab } from '../chat.web'
import { useSymptomsStore } from '@store/Symptoms'
import { ISymptomGroup } from '@store/Symptoms/types'

/* -----------------------------------------------------------------------------
 * SymptomLogGroupItemSeparator
 * -------------------------------------------------------------------------- */

const SymptomLogGroupItemSeparator = ({ height = 1 }) => {
  return (
    <Box px='00' my='00' width='100%' height={height}>
      <Box bg='shade02' flex='1 1 auto' />
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * SymptomLogGroupItem
 * -------------------------------------------------------------------------- */

type SymptomLogGroupItemProps = {
  symptomLogGroup: ISymptomGroup
}

const SymptomLogGroupItem = ({
  symptomLogGroup: { active, start_date, last_end_date, log_count, symptom }
}: SymptomLogGroupItemProps) => {
  const navigate = useNavigate()
  const {
    userId,
    roomId,
    setSelectedLogGroup,
    setActiveTab,
    setIsEventOpened
  } = useContext(ChatContext)

  return (
    <Pressable
      onPress={() => {
        setSelectedLogGroup(symptom.id)
        setActiveTab(ChatTab.TRACKING)
        setIsEventOpened(false)
        if (roomId) {
          navigate(`/chat/${userId}/${roomId}`)
        }
      }}
    >
      <Box
        pt='12'
        pb='12'
        px='16'
        flexDirection='row'
        display='flex'
        justifyContent='space-between'
      >
        <Box>
          <Text font='display.14.bold'>{symptom.name}</Text>
          <Box flexDirection={{ mobile: 'column', tablet: 'row' }}>
            <Text font='text.12.bold' textColor='walrusBlue300_80'>
              Start Date: {moment.utc(start_date).format('MM/DD/YYYY')}
              {'  ·  '.replace(/ /, '\u00a0')}
            </Text>
            <Text font='text.12.bold' textColor='walrusBlue300_80'>
              {active ? 'Last logged' : 'End Date'}:{' '}
              {last_end_date && moment.utc(last_end_date).format('MM/DD/YYYY')}
            </Text>
          </Box>
        </Box>
        <Box display='flex' justifyContent='center'>
          <Text font='text.12.bold' textColor='violet'>
            {log_count} logs
          </Text>
        </Box>
      </Box>
    </Pressable>
  )
}

/* -----------------------------------------------------------------------------
 * SymptomLogGroupList
 * -------------------------------------------------------------------------- */

type SymptomLogGroupListProps = {
  symptomLogGroups: ISymptomGroup[]
}

const SymptomLogGroupList = ({
  symptomLogGroups
}: SymptomLogGroupListProps) => (
  <FlatList
    ItemSeparatorComponent={SymptomLogGroupItemSeparator}
    keyExtractor={(item: ISymptomGroup) => item.symptom.id.toString()}
    data={symptomLogGroups}
    renderItem={({ item }) => (
      <SymptomLogGroupItem symptomLogGroup={item as ISymptomGroup} />
    )}
  />
)

/* -----------------------------------------------------------------------------
 * SymptomLogGroups
 * -------------------------------------------------------------------------- */

const SymptomLogGroups = () => {
  const navigate = useNavigate()
  const {
    userId,
    roomId,
    setActiveTab,
    setSelectedLogGroup,
    setIsEventOpened
  } = useContext(ChatContext)
  const { getSymptomGroups } = useSymptomsStore.getState()
  const [symptomGroups, setSymptomGroups] = useState<ISymptomGroup[]>([])

  useEffect(() => {
    let current = true
    getSymptomGroups(userId).then(groups => {
      if (current) {
        setSymptomGroups(groups)
      }
    })
    return () => {
      current = false
    }
  }, [getSymptomGroups, userId])

  const activeSymptomLogGroups = symptomGroups.filter(group => group.active)
  const inactiveSymptomLogGroups = symptomGroups.filter(group => !group.active)

  // In case of empty state it should be hidden
  if (!symptomGroups.length) {
    return null
  }

  return (
    <Box mt='24' {...accessibilityLabel('SymptomLogGroups')}>
      <Box mb='08'>
        <Text ml='12' mb='08' font='display.16.bold'>
          Logged symptoms and side effects
        </Text>
        <Button
          onPress={() => {
            setActiveTab(ChatTab.TRACKING)
            setSelectedLogGroup(null)
            setIsEventOpened(false)
            if (roomId) {
              navigate(`/chat/${userId}/${roomId}`)
            }
          }}
          size='fit'
          variant='tertiary'
        >
          View logs
        </Button>
      </Box>
      {!!activeSymptomLogGroups.length && (
        <Card px='00' pt='12' pb='00'>
          <Box>
            <Text
              mx='16'
              mb='12'
              font='display.14.bold'
              textColor='walrusBlue200'
            >
              Currently logging ({activeSymptomLogGroups.length})
            </Text>
            <SymptomLogGroupItemSeparator height={2} />
            <SymptomLogGroupList symptomLogGroups={activeSymptomLogGroups} />
          </Box>
        </Card>
      )}

      {!!inactiveSymptomLogGroups.length && (
        <Card px='00' pt='12' mt='20' pb='00'>
          <Box>
            <Text
              mx='16'
              mb='12'
              font='display.14.bold'
              textColor='walrusBlue200'
            >
              Previously logged ({inactiveSymptomLogGroups.length})
            </Text>
            <SymptomLogGroupItemSeparator height={2} />
            <SymptomLogGroupList symptomLogGroups={inactiveSymptomLogGroups} />
          </Box>
        </Card>
      )}
    </Box>
  )
}

export default SymptomLogGroups
